import { useMutation } from '@tanstack/react-query';
import { isArray, omit, toString } from 'lodash';
import { queryClient } from 'src/components/queryProvider';
import { COLLECTIONS, USER_ACTIONS } from 'src/constants/constants';
import { narrativeGenerators } from 'src/hooks/api/userActions/helper';
import { IAssemblyComponent } from 'src/interfaces';
import { queryKeys } from 'src/utils/queryKeys';

import { requestCCApi } from '../../../utils/axios-utils';
import { findDeleteAssemblyComponents } from './helpers';
import { fetchAssemblyComponents } from './useGetAssemblyComponents';

enum HttpMethod {
  POST = 'POST',
  PATCH = 'PATCH',
  DELETE = 'DELETE',
}

interface TransactionPayload {
  resource: string;
  method: HttpMethod;
  id?: number;
  data?: Record<string, any>;
}

const createAssemblyComponentNarrativeFn = narrativeGenerators[
  COLLECTIONS.ASSEMBLIES_COMPONENTS
][USER_ACTIONS.CREATE] as (
  assemblyName: string,
  componentName: string,
) => string;
const updateAssemblyComponentNarrativeFn = narrativeGenerators[
  COLLECTIONS.ASSEMBLIES_COMPONENTS
][USER_ACTIONS.UPDATE] as (
  assemblyName: string,
  componentName: string,
) => string;
const deleteAssemblyComponentNarrativeFn = narrativeGenerators[
  COLLECTIONS.ASSEMBLIES_COMPONENTS
][USER_ACTIONS.DELETE] as (
  assemblyName: string,
  componentName: string,
) => string;

export const useUpdateCreateDeleteAssemblyComponents = () => {
  return useMutation({
    mutationFn: updateCreateDeleteAssemblyComponents,
    onSuccess: () => {
      queryClient.invalidateQueries(
        queryKeys.assemblyComponents.invalidateAll(),
      );
      queryClient.invalidateQueries(
        queryKeys.viewAssembliesWithComponents.invalidateAll(),
      );
      queryClient.invalidateQueries(
        queryKeys.bulkReplaceEligibleAssemblyIds.invalidateAll(),
      );
      queryClient.invalidateQueries(
        queryKeys.scheduleAssembliesSearch.invalidateAll(),
      );
      queryClient.invalidateQueries(queryKeys.components.invalidateAll());
      queryClient.invalidateQueries(queryKeys.componentListing.invalidateAll());
    },
  });
};

const updateCreateDeleteAssemblyComponents = async (payload: {
  assemblyId: number;
  data: Partial<IAssemblyComponent>[];
  assemblyName?: string;
}) => {
  const { data: originalAssemblyComponents, backupAssemblyComponents } =
    await fetchAssemblyComponents(toString(payload.assemblyId));

  const transactionPayload: TransactionPayload[] = [];

  // Handle deletions
  const deleteItems = findDeleteAssemblyComponents(
    originalAssemblyComponents,
    backupAssemblyComponents,
    payload.data,
  );

  deleteItems.forEach((item) => {
    const narrative = deleteAssemblyComponentNarrativeFn(
      payload?.assemblyName ?? '',
      item?.component?.name ?? '',
    );

    transactionPayload.push({
      resource: COLLECTIONS.ASSEMBLIES_COMPONENTS,
      method: HttpMethod.DELETE,
      id: item.id,
      data: { narrative },
    });
  });

  // Handle updates
  payload.data
    .filter((assemblyComponent) => assemblyComponent.id)
    .forEach((item) => {
      transactionPayload.push({
        resource: COLLECTIONS.ASSEMBLIES_COMPONENTS,
        method: HttpMethod.PATCH,
        id: item.id,
        data: {
          ...sanitizedData(item),
          narrative: updateAssemblyComponentNarrativeFn(
            payload?.assemblyName ?? '',
            item?.component?.name ?? '',
          ),
        },
      });
    });

  // Handle creations
  payload.data
    .filter((item) => !item.id)
    .forEach((item) => {
      transactionPayload.push({
        resource: COLLECTIONS.ASSEMBLIES_COMPONENTS,
        method: HttpMethod.POST,
        data: {
          ...sanitizedData(item),
          narrative: createAssemblyComponentNarrativeFn(
            payload?.assemblyName ?? '',
            item?.component?.name ?? '',
          ),
        },
      });
    });

  return requestCCApi({
    url: '/transaction',
    method: HttpMethod.POST,
    data: { data: transactionPayload },
  });
};

const sanitizedData = (
  payload: Partial<IAssemblyComponent> | Partial<IAssemblyComponent>[],
) => {
  const omitFields = ['id', 'created_at', 'modified_at', 'uid', 'component'];

  return isArray(payload)
    ? payload.map((item) => omit(item, omitFields))
    : omit(payload, omitFields);
};
